import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faTimes, faSpinner, faCheckCircle, faExclamationTriangle,
  faCloudUpload, faLink, faTable, faRobot, faChevronRight, faChevronLeft,
  faFileImport, faSync, faSignInAlt, faSignOutAlt, faUser, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import intelligentCsvParser, { ParsedData } from '../services/intelligentCsvParser';
import googleSheetsService, { GoogleSheetsData, SpreadsheetInfo } from '../services/googleSheetsService';
import { BusinessMetric } from '../types/businessMetrics';

interface SmartImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (metrics: BusinessMetric[]) => void;
  selectedMonth?: string;
}

type ImportSource = 'file' | 'google-sheets' | 'url';
type WizardStep = 'source' | 'upload' | 'parse' | 'preview' | 'confirm';

export const SmartImportWizard: React.FC<SmartImportWizardProps> = ({
  isOpen,
  onClose,
  onImport,
  selectedMonth
}) => {
  const [step, setStep] = useState<WizardStep>('source');
  const [importSource, setImportSource] = useState<ImportSource>('file');
  const [file, setFile] = useState<File | null>(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [selectedSheet, setSelectedSheet] = useState('');
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Google Sheets specific state
  const [isGoogleSignedIn, setIsGoogleSignedIn] = useState(false);
  const [allSpreadsheets, setAllSpreadsheets] = useState<SpreadsheetInfo[]>([]);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState<SpreadsheetInfo | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Reset wizard
  const resetWizard = () => {
    setStep('source');
    setFile(null);
    setGoogleSheetUrl('');
    setParsedData(null);
    setError(null);
  };

  // Handle file selection
  const handleFileSelect = async (selectedFile: File) => {
    setError(null);
    setFile(selectedFile);
    setImporting(true);

    try {
      const text = await selectedFile.text();
      const parsed = await intelligentCsvParser.parse(text);
      setParsedData(parsed);
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Failed to parse file');
    } finally {
      setImporting(false);
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    setError(null);
    setImporting(true);

    try {
      await googleSheetsService.authenticate();
      setIsGoogleSignedIn(true);

      const email = googleSheetsService.getCurrentUserEmail();
      setUserEmail(email);

      // Load all spreadsheets
      const spreadsheets = await googleSheetsService.listAllSpreadsheets();
      setAllSpreadsheets(spreadsheets);
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google');
      setIsGoogleSignedIn(false);
    } finally {
      setImporting(false);
    }
  };

  // Handle Google Sign Out
  const handleGoogleSignOut = async () => {
    try {
      await googleSheetsService.signOut();
      setIsGoogleSignedIn(false);
      setUserEmail(null);
      setAllSpreadsheets([]);
      setSelectedSpreadsheet(null);
      setAvailableSheets([]);
    } catch (err: any) {
      console.error('Error signing out:', err);
    }
  };

  // Handle spreadsheet selection
  const handleSelectSpreadsheet = async (spreadsheet: SpreadsheetInfo) => {
    setError(null);
    setImporting(true);
    setSelectedSpreadsheet(spreadsheet);

    try {
      const sheets = await googleSheetsService.listSheets(spreadsheet.id);
      setAvailableSheets(sheets);
      setSelectedSheet(sheets[0] || '');
    } catch (err: any) {
      setError(err.message || 'Failed to load sheets');
    } finally {
      setImporting(false);
    }
  };

  // Fetch data from selected Google Sheet
  const handleFetchGoogleSheet = async () => {
    if (!selectedSpreadsheet || !selectedSheet) {
      setError('Please select a spreadsheet and sheet');
      return;
    }

    setError(null);
    setImporting(true);

    try {
      const data = await googleSheetsService.fetchSheetData(selectedSpreadsheet.id, selectedSheet);
      const csvText = googleSheetsService.convertToCSV(data);
      const parsed = await intelligentCsvParser.parse(csvText);
      setParsedData(parsed);
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch Google Sheets data');
    } finally {
      setImporting(false);
    }
  };

  // Handle final import
  const handleFinalImport = async () => {
    if (!parsedData) return;

    setImporting(true);
    try {
      // Get filename from file object or Google Sheets selection
      const fileName = file?.name || selectedSheet || 'imported_data.csv';

      const metrics = intelligentCsvParser.convertToBusinessMetrics(parsedData, selectedMonth, fileName);
      console.log('📊 Converted metrics:', metrics.length, 'metrics');
      console.log('📁 CSV Source:', fileName);
      console.log('📅 Selected month:', selectedMonth);
      console.log('📋 Sample metric:', metrics[0]);

      if (metrics.length === 0) {
        throw new Error('No metrics to import. Try removing the month filter or check your CSV date format.');
      }

      await onImport(metrics as any);
      onClose();
      resetWizard();
    } catch (err: any) {
      setError(err.message || 'Failed to import metrics');
    } finally {
      setImporting(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-300 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faRobot} className="mr-2 text-purple-600" />
              Smart Data Import Wizard
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              Intelligently import data from CSV files or Google Sheets
            </p>
          </div>
          <button onClick={() => { onClose(); resetWizard(); }} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {['Source', 'Upload', 'Preview', 'Confirm'].map((label, idx) => {
              const stepNames: WizardStep[] = ['source', 'upload', 'preview', 'confirm'];
              const currentIndex = stepNames.indexOf(step);
              const isActive = idx === currentIndex;
              const isCompleted = idx < currentIndex;

              return (
                <div key={label} className="flex items-center">
                  <div className={`flex items-center ${idx > 0 ? 'ml-4' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isCompleted ? 'bg-green-500 text-white' :
                      isActive ? 'bg-purple-600 text-white' :
                      'bg-gray-300 text-gray-600'
                    }`}>
                      {isCompleted ? <FontAwesomeIcon icon={faCheckCircle} /> : idx + 1}
                    </div>
                    <span className={`ml-2 text-xs font-medium ${
                      isActive ? 'text-purple-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {label}
                    </span>
                  </div>
                  {idx < 3 && (
                    <FontAwesomeIcon icon={faChevronRight} className="mx-2 text-gray-400 text-xs" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center space-x-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
              <span className="text-xs text-red-800">{error}</span>
            </div>
          )}

          {/* Step 1: Choose Source */}
          {step === 'source' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Choose Your Data Source</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* CSV/Excel File */}
                <button
                  onClick={() => { setImportSource('file'); setStep('upload'); }}
                  className="p-6 border-2 border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <FontAwesomeIcon icon={faFileImport} className="text-3xl text-purple-600 mb-3" />
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Upload File</h4>
                  <p className="text-xs text-gray-600">
                    Upload CSV or Excel file from your computer. Supports any format with metrics in rows or columns.
                  </p>
                </button>

                {/* Google Sheets */}
                <button
                  onClick={() => { setImportSource('google-sheets'); setStep('upload'); }}
                  className="p-6 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                >
                  <FontAwesomeIcon icon={faGoogle} className="text-3xl text-green-600 mb-3" />
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Google Sheets</h4>
                  <p className="text-xs text-gray-600">
                    Connect directly to Google Sheets. Auto-sync data and pull updates automatically.
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Upload/Connect */}
          {step === 'upload' && (
            <div className="space-y-4">
              {importSource === 'file' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Upload Your File</h3>

                  <div
                    className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                      dragActive ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <FontAwesomeIcon icon={faCloudUpload} className="text-5xl text-gray-400 mb-4" />
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Drag & drop your file here or click to browse
                    </h4>
                    <p className="text-xs text-gray-600 mb-4">
                      Supports CSV, Excel (.xlsx), and text files
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <FontAwesomeIcon icon={faUpload} className="mr-2" />
                      Choose File
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls,.txt"
                      onChange={(e) => e.target.files && handleFileSelect(e.target.files[0])}
                      className="hidden"
                    />
                  </div>

                  {importing && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded flex items-center space-x-3">
                      <FontAwesomeIcon icon={faSpinner} spin className="text-blue-600" />
                      <span className="text-xs text-blue-800">Parsing file with AI...</span>
                    </div>
                  )}
                </div>
              )}

              {importSource === 'google-sheets' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Connect to Google Sheets</h3>

                  {!isGoogleSignedIn ? (
                    // Sign In Section
                    <div className="space-y-4">
                      <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg text-center">
                        <FontAwesomeIcon icon={faGoogle} className="text-5xl text-green-600 mb-4" />
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          Sign in with Google
                        </h4>
                        <p className="text-xs text-gray-600 mb-4">
                          Authorize once to access all your Google Sheets
                        </p>
                        <button
                          onClick={handleGoogleSignIn}
                          disabled={importing}
                          className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                        >
                          {importing ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faGoogle} className="mr-2 text-green-600" />
                              Sign in with Google
                            </>
                          )}
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 text-center">
                        <p>✓ Read-only access to your spreadsheets</p>
                        <p>✓ Secure OAuth authentication</p>
                        <p>✓ Authorization lasts for this session</p>
                      </div>
                    </div>
                  ) : (
                    // Signed In - Show Spreadsheets
                    <div className="space-y-4">
                      {/* User Info Bar */}
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faUser} className="text-green-600" />
                          <span className="text-xs font-medium text-green-900">{userEmail || 'Signed in'}</span>
                        </div>
                        <button
                          onClick={handleGoogleSignOut}
                          className="text-xs text-green-700 hover:text-green-900 flex items-center space-x-1"
                        >
                          <FontAwesomeIcon icon={faSignOutAlt} />
                          <span>Sign out</span>
                        </button>
                      </div>

                      {/* Spreadsheet Selection */}
                      {!selectedSpreadsheet ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Select a Spreadsheet ({allSpreadsheets.length} available)
                          </label>
                          <div className="border border-gray-300 rounded-lg max-h-80 overflow-y-auto">
                            {allSpreadsheets.map((sheet) => (
                              <button
                                key={sheet.id}
                                onClick={() => handleSelectSpreadsheet(sheet)}
                                className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-200 last:border-b-0 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{sheet.name}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Modified: {new Date(sheet.modifiedTime).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <FontAwesomeIcon icon={faChevronRight} className="text-gray-400" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        // Selected Spreadsheet - Show Sheets
                        <div className="space-y-4">
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-xs font-semibold text-blue-900">Selected Spreadsheet</div>
                                <div className="text-sm font-medium text-blue-900 mt-1">{selectedSpreadsheet.name}</div>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedSpreadsheet(null);
                                  setAvailableSheets([]);
                                }}
                                className="text-xs text-blue-700 hover:text-blue-900"
                              >
                                Change
                              </button>
                            </div>
                          </div>

                          {availableSheets.length > 0 && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Select Sheet Tab
                              </label>
                              <select
                                value={selectedSheet}
                                onChange={(e) => setSelectedSheet(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                              >
                                {availableSheets.map(sheet => (
                                  <option key={sheet} value={sheet}>{sheet}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          <button
                            onClick={handleFetchGoogleSheet}
                            disabled={importing || !selectedSheet}
                            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {importing ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                Import Sheet Data
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview Parsed Data */}
          {step === 'preview' && parsedData && (
            <div className="space-y-4">
              {/* Import Month Warning */}
              <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-lg" />
                  <div>
                    <div className="text-sm font-bold text-blue-900">
                      Data will be imported to: {selectedMonth || 'Current Month'}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Make sure you're viewing the correct month in Unit Economics to see your imported data
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Data Preview</h3>
                <div className="flex items-center space-x-2 text-xs">
                  <span className={`px-3 py-1 rounded-full ${
                    parsedData.confidence > 0.8 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {(parsedData.confidence * 100).toFixed(0)}% confidence
                  </span>
                </div>
              </div>

              {/* Detection Summary */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <FontAwesomeIcon icon={faRobot} className="text-purple-600" />
                  <span className="text-xs font-semibold text-purple-900">AI Detection Results</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-gray-600">Format:</span>
                    <span className="font-medium text-gray-900 ml-2">{parsedData.summary.detectedFormat}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Metrics:</span>
                    <span className="font-medium text-gray-900 ml-2">{parsedData.summary.totalMetrics}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Data Points:</span>
                    <span className="font-medium text-gray-900 ml-2">{parsedData.summary.totalDataPoints}</span>
                  </div>
                </div>
              </div>

              {/* Data Table Preview */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Metric</th>
                        {parsedData.timePeriods.slice(0, 5).map(period => (
                          <th key={period} className="px-4 py-2 text-right font-semibold text-gray-700 border-b">
                            {period}
                          </th>
                        ))}
                        {parsedData.timePeriods.length > 5 && (
                          <th className="px-4 py-2 text-right font-semibold text-gray-700 border-b">...</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.metrics.map((metric, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 border-b">
                          <td className="px-4 py-2 font-medium text-gray-900">
                            {metric.metricName}
                            <span className="ml-2 text-gray-500 text-xs">({metric.unit})</span>
                          </td>
                          {parsedData.timePeriods.slice(0, 5).map(period => (
                            <td key={period} className="px-4 py-2 text-right text-gray-900">
                              {metric.values[period]?.toLocaleString() || '-'}
                            </td>
                          ))}
                          {parsedData.timePeriods.length > 5 && (
                            <td className="px-4 py-2 text-right text-gray-500">...</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-300 flex justify-between">
          <button
            onClick={() => {
              if (step === 'upload') setStep('source');
              else if (step === 'preview') setStep('upload');
              else { onClose(); resetWizard(); }
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
            {step === 'source' ? 'Cancel' : 'Back'}
          </button>

          {step === 'preview' && (
            <button
              onClick={handleFinalImport}
              disabled={importing}
              className="px-6 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Import {parsedData?.summary.totalDataPoints} Data Points
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartImportWizard;
